(() => {
  const CART_PATHS = ['/cart/add.js', '/cart/update.js', '/cart/change.js'];
  const ADD_TIMEOUT_MS = 8000;
  const CART_MUTATED = 'hira:cart-mutated';
  const EVT_PREFIX = 'hira-product-form:';
  const EMBLA_INIT_EVENT = 'hira:embla-init';
  const GALLERY_ZOOM_MQ = '(min-width: 768px) and (hover: hover) and (pointer: fine)';
  const VIDEO_TYPES = new Set(['video', 'external_video']);
  const EXTERNAL_VIDEO_SRC = {
    youtube: (id) => `https://www.youtube.com/embed/${id}?autoplay=1&playsinline=1&rel=0&controls=0`,
    vimeo: (id) => `https://player.vimeo.com/video/${id}?autoplay=1&playsinline=1&controls=0`,
  };

  // CartX exposes no event, callback or promise for a completed add, so cart
  // mutations are observed at the network layer instead. The wrapper is purely
  // additive: it calls through to whatever fetch it found and never unpatches.
  if (!window.__hiraCartFetchObserved) {
    window.__hiraCartFetchObserved = true;

    const prevFetch = window.fetch;

    // Named (not anonymous) so CartX's stack sniff in cartx.js:1-21 — which
    // blocks frames containing "at <anonymous>" or "VM" — lets calls through.
    const hiraCartFetchObserver = (...args) => {
      const request = prevFetch.apply(window, args);
      const [resource] = args;
      const url = typeof resource === 'string' ? resource : resource?.url ?? '';

      if (!CART_PATHS.some((path) => url.includes(path))) return request;

      request
        .then((response) =>
          response
            .clone()
            .json()
            .then((cart) => ({ ok: response.ok, cart }))
            .catch((error) => ({ ok: false, error })),
        )
        .catch((error) => ({ ok: false, error }))
        .then((detail) => document.dispatchEvent(new CustomEvent(CART_MUTATED, { detail })));

      return request;
    };

    window.fetch = hiraCartFetchObserver;
  }

  class HiraProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-hira-variant-id]');
      this.quantityInput = this.querySelector('[data-hira-quantity]');
      this.productData = null;
      this.pendingAdd = null;
      this.addTimeoutId = null;

      const payload = this.querySelector('[data-hira-product]')?.textContent;
      if (payload) {
        try {
          this.productData = JSON.parse(payload);
        } catch (error) {
          console.warn('[hira-product-form] product payload could not be parsed', error);
        }
      }

      this.variantId = Number(this.variantInput?.value) || this.productData?.selected_variant_id || 0;
      this.quantity = 1;
      this.available = this.findVariant(this.variantId)?.available ?? true;

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleCartxClick = this.handleCartxClick.bind(this);
      this.handleAddRequest = this.handleAddRequest.bind(this);
      this.handleCartMutated = this.handleCartMutated.bind(this);

      this.form?.addEventListener('submit', this.handleSubmit);
      this.addEventListener('change', this.handleVariantChange);
      this.addEventListener('click', this.handleCartxClick, true);
      this.addEventListener(`${EVT_PREFIX}add-to-cart`, this.handleAddRequest);
      document.addEventListener(CART_MUTATED, this.handleCartMutated);
    }

    disconnectedCallback() {
      clearTimeout(this.addTimeoutId);
      this.addTimeoutId = null;
      this.pendingAdd = null;
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.removeEventListener('change', this.handleVariantChange);
      this.removeEventListener('click', this.handleCartxClick, true);
      this.removeEventListener(`${EVT_PREFIX}add-to-cart`, this.handleAddRequest);
      document.removeEventListener(CART_MUTATED, this.handleCartMutated);
    }

    getState() {
      return { variantId: this.variantId, quantity: this.quantity, available: this.available };
    }

    setVariant(id) {
      const nextId = Number(id);
      if (!Number.isFinite(nextId) || nextId === this.variantId) return;

      const variant = this.findVariant(nextId);
      if (!variant) return;

      this.variantId = nextId;
      this.available = variant.available;
      if (this.variantInput) this.variantInput.value = String(nextId);
      this.syncCartxWrapper();
      this.emit('variant-changed', {
        variantId: nextId,
        variant,
        available: this.available,
        quantity: this.quantity,
      });
    }

    setQuantity(value) {
      const requested = Number(value);
      if (!Number.isFinite(requested)) return;

      let next = Math.max(1, Math.floor(requested));
      const variant = this.findVariant(this.variantId);
      if (variant && variant.inventory_policy !== 'continue' && variant.inventory_quantity > 0) {
        next = Math.min(next, variant.inventory_quantity);
      }
      if (next === this.quantity) return;

      this.quantity = next;
      if (this.quantityInput) this.quantityInput.value = String(next);
      this.syncCartxWrapper();
      this.emit('qty-changed', { quantity: next, variantId: this.variantId });
    }

    findVariant(id) {
      const target = Number(id);
      return this.productData?.variants?.find((variant) => variant.id === target) ?? null;
    }

    // The CartX button wrapper arrives with the price/ATC block (PRD 2.4); until
    // then this is a guarded no-op rather than a second source of truth.
    syncCartxWrapper() {
      const wrapper = this.querySelector('.cartx-product-button-wrap');
      if (!wrapper) return;

      const variant = this.findVariant(this.variantId);
      wrapper.dataset.variantId = String(this.variantId);
      wrapper.dataset.quantity = String(this.quantity);
      if (variant) wrapper.dataset.price = String(variant.price);
    }

    emit(name, detail) {
      this.dispatchEvent(new CustomEvent(`${EVT_PREFIX}${name}`, { bubbles: true, detail }));
    }

    handleSubmit(event) {
      event.preventDefault();
      this.emit('add-to-cart', { variantId: this.variantId, quantity: this.quantity });
    }

    handleVariantChange(event) {
      const { target } = event;
      if (target === this.variantInput) return;
      if (!target.matches?.('select[name="id"], input[name="id"]')) return;

      this.setVariant(target.value);
    }

    // Capture phase and no preventDefault: CartX's own bubble-phase handler must
    // still run. Claiming the pending slot first stops handleAddRequest from
    // issuing a second add for the same click.
    handleCartxClick(event) {
      if (this.pendingAdd) return;
      if (!event.target.closest?.('.cartx-add-to-cart')) return;

      this.beginPendingAdd();
      this.emit('add-to-cart', { variantId: this.variantId, quantity: this.quantity });
    }

    handleAddRequest() {
      if (this.pendingAdd) return;

      this.routeAddToCart();
    }

    routeAddToCart() {
      const { variantId, quantity } = this;

      if (typeof window.cartx !== 'function') {
        this.emit('add-to-cart-error', { variantId, quantity, error: { reason: 'cartx-unavailable' } });
        return;
      }

      this.beginPendingAdd();
      window.cartx(
        {
          [String(variantId)]: {
            quantity,
            price: this.findVariant(variantId)?.price ?? 0,
            type: this.productData?.type ?? '',
            collection_ids: this.productData?.collection_ids ?? [],
          },
        },
        'ADD',
        {},
      );
    }

    // CartX's error path is silent — it clears neither the loading class nor any
    // flag — so the timeout is the only way an add can be reported as failed.
    beginPendingAdd() {
      this.pendingAdd = { variantId: this.variantId, quantity: this.quantity };
      clearTimeout(this.addTimeoutId);
      this.addTimeoutId = setTimeout(() => {
        const pending = this.pendingAdd;
        this.addTimeoutId = null;
        this.pendingAdd = null;
        if (pending) this.emit('add-to-cart-error', { ...pending, error: { reason: 'timeout' } });
      }, ADD_TIMEOUT_MS);
    }

    handleCartMutated(event) {
      const { ok, cart, error } = event.detail ?? {};

      // `item_count` is the only key header.js:216-235 reads.
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { item_count: cart?.item_count ?? 0, cart } }),
      );

      const pending = this.pendingAdd;
      if (!pending) return;

      clearTimeout(this.addTimeoutId);
      this.addTimeoutId = null;
      this.pendingAdd = null;

      if (ok) this.emit('add-to-cart-success', { ...pending, cart });
      else this.emit('add-to-cart-error', { ...pending, error: error ?? { reason: 'cart-error' } });
    }
  }

  class HiraHeroGallery extends HTMLElement {
    connectedCallback() {
      this.carousel = this.querySelector('.hira-carousel');
      this.rail = this.querySelector('.hira-gallery__rail');
      this.thumbs = [...this.querySelectorAll('[data-hira-gallery-thumb]')];
      this.slides = [...this.querySelectorAll('[data-hira-gallery-index]')];
      this.embla = null;
      this.activeMount = null;
      this.zoomMq = null;

      this.onSelect = this.onSelect.bind(this);
      this.onEmblaInit = this.onEmblaInit.bind(this);
      this.onThumbClick = this.onThumbClick.bind(this);
      this.onRailKeydown = this.onRailKeydown.bind(this);
      this.onZoomEnter = this.onZoomEnter.bind(this);
      this.onZoomMove = this.onZoomMove.bind(this);
      this.onZoomLeave = this.onZoomLeave.bind(this);
      this.onZoomMqChange = this.onZoomMqChange.bind(this);

      this.rail?.addEventListener('click', this.onThumbClick);
      this.rail?.addEventListener('keydown', this.onRailKeydown);

      if (this.hasAttribute('data-hira-gallery-zoom')) {
        this.zoomMq = window.matchMedia(GALLERY_ZOOM_MQ);
        this.zoomMq.addEventListener('change', this.onZoomMqChange);
        this.addEventListener('mouseenter', this.onZoomEnter);
        this.addEventListener('mousemove', this.onZoomMove);
        this.addEventListener('mouseleave', this.onZoomLeave);
      }

      this.syncTabindex(0);

      if (!this.carousel) {
        console.warn('[hira-hero-gallery] missing .hira-carousel — carousel controls disabled');
        return;
      }

      // hira-embla.js is a module and this file is a classic deferred script, so
      // either can run first. Adopt an existing instance, otherwise wait for its
      // init event.
      const existing = window.hiraEmbla?.get(this.carousel);
      if (existing) {
        this.adoptEmbla(existing);
        return;
      }

      this.carousel.addEventListener(EMBLA_INIT_EVENT, this.onEmblaInit, { once: true });

      // hira-embla.js only scans on DOMContentLoaded and shopify:section:load, so
      // markup swapped in by anything else (Section Rendering API, the CLI dev
      // server's hot reload) would never be picked up. Asking for our own root
      // makes the gallery independent of who scanned and when; initOne is
      // idempotent, and this is a no-op until the module has executed.
      window.hiraEmbla?.init(this.carousel);
    }

    disconnectedCallback() {
      this.embla?.off('select', this.onSelect);
      this.embla = null;
      this.carousel?.removeEventListener(EMBLA_INIT_EVENT, this.onEmblaInit);
      this.rail?.removeEventListener('click', this.onThumbClick);
      this.rail?.removeEventListener('keydown', this.onRailKeydown);
      this.zoomMq?.removeEventListener('change', this.onZoomMqChange);
      this.removeEventListener('mouseenter', this.onZoomEnter);
      this.removeEventListener('mousemove', this.onZoomMove);
      this.removeEventListener('mouseleave', this.onZoomLeave);
      this.teardownVideo();
    }

    onEmblaInit(event) {
      this.adoptEmbla(event.detail?.api);
    }

    adoptEmbla(api) {
      if (!api) return;
      this.embla = api;
      api.on('select', this.onSelect);
      this.onSelect();
    }

    onSelect() {
      const index = this.embla?.selectedScrollSnap() ?? 0;

      for (const slide of this.slides) {
        slide.classList.toggle('is-selected', slide.dataset.hiraGalleryIndex === String(index));
      }
      for (const thumb of this.thumbs) {
        thumb.classList.toggle('is-active', thumb.dataset.hiraGalleryThumb === String(index));
      }
      this.syncTabindex(index);

      // A video plays as soon as its slide becomes the active one, and stops the
      // moment it stops being active. Nothing is fetched until the shopper
      // actually reaches the slide, so page load is still untouched.
      const slide = this.slides[index];
      if (VIDEO_TYPES.has(slide?.dataset.hiraGalleryType)) this.activateVideo(slide);
      else this.teardownVideo();
    }

    // Roving tabindex: the rail is one tab stop, arrow keys move within it.
    syncTabindex(index) {
      if (this.thumbs.length === 0) return;

      const active = this.thumbs.find((thumb) => thumb.dataset.hiraGalleryThumb === String(index));
      const target = active ?? this.thumbs[0];
      for (const thumb of this.thumbs) {
        thumb.tabIndex = thumb === target ? 0 : -1;
      }
    }

    onThumbClick(event) {
      const thumb = event.target.closest('[data-hira-gallery-thumb]');
      if (!thumb) return;

      const index = Number(thumb.dataset.hiraGalleryThumb);
      this.embla?.scrollTo(index);

      if (thumb.hasAttribute('data-hira-gallery-thumb-play')) {
        this.activateVideo(this.slides[index]);
      }
    }

    onRailKeydown(event) {
      const current = this.thumbs.indexOf(event.target.closest('[data-hira-gallery-thumb]'));
      if (current === -1) return;

      const last = this.thumbs.length - 1;
      let next = current;

      if (event.key === 'ArrowLeft') next = current === 0 ? last : current - 1;
      else if (event.key === 'ArrowRight') next = current === last ? 0 : current + 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      else return;

      event.preventDefault();
      for (const thumb of this.thumbs) thumb.tabIndex = -1;
      this.thumbs[next].tabIndex = 0;
      this.thumbs[next].focus();
    }


    // Nothing about a video is fetched until this runs — reaching the slide is
    // the only thing that ever constructs the <video>/<iframe>.
    activateVideo(slide) {
      const mount = slide?.querySelector('[data-hira-gallery-mount]');
      const payload = slide?.querySelector('[data-hira-gallery-video]')?.textContent;
      if (!mount || !payload) return;

      // Already playing this slide — `select` can fire again (reInit, resize)
      // and remounting would restart playback from zero.
      if (this.activeMount === mount) return;

      let data;
      try {
        data = JSON.parse(payload);
      } catch (error) {
        console.warn('[hira-hero-gallery] video payload could not be parsed', error);
        return;
      }

      this.teardownVideo();

      if (data.type === 'video') {
        const video = document.createElement('video');
        video.controls = false;
        video.autoplay = true;
        video.preload = 'none';
        video.playsInline = true;
        for (const source of data.sources ?? []) {
          const sourceEl = document.createElement('source');
          sourceEl.src = source.url;
          sourceEl.type = source.mime_type;
          video.appendChild(sourceEl);
        }
        mount.appendChild(video);
        // Activation is always a user gesture, so unmuted playback is permitted.
        // With controls off there is no manual start, so a rejected play would
        // leave a dead frame — fall back to muted, which is always allowed.
        video.play().catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });
      } else if (data.type === 'external_video') {
        const src = EXTERNAL_VIDEO_SRC[data.host]?.(data.external_id);
        if (!src) {
          console.warn('[hira-hero-gallery] unsupported video host', data.host);
          return;
        }

        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.title = data.alt ?? '';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        iframe.loading = 'lazy';
        iframe.frameBorder = '0';
        mount.appendChild(iframe);
      } else {
        return;
      }

      mount.classList.add('is-active');
      this.activeMount = mount;
    }

    // Emptying the mount is what stops a YouTube/Vimeo player — clearing the
    // iframe src alone leaves an audible player in some browsers.
    teardownVideo() {
      if (!this.activeMount) return;

      this.activeMount.querySelector('video')?.pause();
      this.activeMount.replaceChildren();
      this.activeMount.classList.remove('is-active');
      this.activeMount = null;
    }

    onZoomEnter() {
      if (this.zoomMq?.matches) this.classList.add('is-zoomed');
    }

    onZoomMove(event) {
      if (!this.classList.contains('is-zoomed')) return;

      // Read per move rather than caching: .hira-hero__gallery is sticky, so the
      // media box shifts under the cursor as the column pins.
      const media = this.querySelector('.hira-gallery__slide.is-selected .hira-gallery__media');
      const rect = media?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;

      const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
      this.style.setProperty('--hira-gallery-zoom-x', `${x}%`);
      this.style.setProperty('--hira-gallery-zoom-y', `${y}%`);
    }

    onZoomLeave() {
      this.classList.remove('is-zoomed');
    }

    onZoomMqChange(event) {
      if (!event.matches) this.classList.remove('is-zoomed');
    }
  }

  if (!customElements.get('hira-product-form')) {
    customElements.define('hira-product-form', HiraProductForm);
  }

  if (!customElements.get('hira-hero-gallery')) {
    customElements.define('hira-hero-gallery', HiraHeroGallery);
  }
})();
